/**
 * VdoCipher (`video_type: "flexi"`) DRM playback.
 *
 * Reference: Course_Membership/src/components/VdoPlayer.js — the legacy learner
 * app's player. It does four things, and this module does all four plus the two
 * the old one never had:
 *
 *   legacy  1. render lesson.embed verbatim (the server-signed <iframe>)
 *           2. inject https://player.vdocipher.com/v2/api.js
 *           3. VdoPlayer.getInstance(iframe) -> player, then read
 *              player.video.currentTime on `play` and `timeupdate`
 *           4. show "This video is currently unavailable." with no embed
 *   added   5. RESUME from the member's saved position
 *           6. PERSIST that position back, on the same cadence as every other
 *              player in this app (10s while playing, plus on pause/ended/exit)
 *
 * Why a signed embed at all: VdoCipher will not play from a bare video id. Every
 * playback needs a fresh, short-lived OTP minted server-side with the API
 * secret, which is why the browser can only ever render what api/lesson-details
 * hands it in `embed` — there is no client-side route to a VdoCipher video, and
 * the legacy app has no endpoint for one either.
 *
 * DRM specifics that decide whether this works AT ALL in a given browser:
 *   • the <iframe> must be granted `encrypted-media`, or the EME handshake the
 *     Widevine/FairPlay licence needs never happens (see videoEmbed.js)
 *   • the page must be a secure context — EME is unavailable over plain http
 *
 * The returned handle is deliberately the SAME shape makeEmbedTimeTracker
 * returns ({ getSeconds, seekTo, destroy }), so the player, the note
 * timestamps and the seek-from-a-note action all talk to one object and there
 * is only ever ONE VdoCipher instance per iframe.
 */

import { loadVdoCipherApi } from "./videoTime";

/** Save at most this often while playing — matches ResumableVideo / HlsVideo. */
const SAVE_EVERY_MS = 10000;
/** Never resume this close to the end; it would drop the member on the credits. */
const RESUME_TAIL_S = 2;
/** Only resume while playback is still at the very start, so a member who
 *  scrubbed before the player was ready is never yanked backwards. */
const RESUME_WINDOW_S = 1;

/**
 * Attach to a rendered VdoCipher embed.
 *
 * @param {HTMLIFrameElement} iframe   the <iframe> inside the signed embed
 * @param {object}   opts
 * @param {number|Function} opts.startAt  seconds to resume from (0 = from the
 *        top). May be a getter, because the member's saved positions are
 *        fetched in parallel with the lesson and can land after the player
 *        does — a fixed number read too early silently means "start over".
 * @param {Function} opts.onSave       (positionSeconds, durationSeconds|null)
 * @param {Function} opts.onReady      the player answered — it works here
 * @param {Function} opts.onUnknown    the API never loaded; nothing is known
 * @returns {{getSeconds:Function, seekTo:Function, destroy:Function}|null}
 */
export function attachVdoCipher(iframe, opts = {}) {
    if (!iframe) return null;

    const { startAt = 0, onSave, onReady, onUnknown } = opts;

    let seconds = null;      // last known position, kept while paused
    let duration = null;
    let media = null;        // player.video — a media element
    let destroyed = false;
    let resumed = false;
    let lastSaveAt = 0;
    let cleanup = () => {};

    // One verdict only, and never after destroy() — same contract as
    // makeEmbedTimeTracker, which the embed health watchdog relies on.
    let verdictSent = false;
    const markReady = () => {
        if (verdictSent || destroyed) return;
        verdictSent = true;
        try { onReady?.(); } catch { /* ignore */ }
    };
    const markUnknown = () => {
        if (verdictSent || destroyed) return;
        verdictSent = true;
        try { onUnknown?.(); } catch { /* ignore */ }
    };

    /** Refresh the cached position/duration from the player. */
    const readNow = () => {
        if (!media) return;
        try {
            const t = media.currentTime;
            if (typeof t === "number" && !Number.isNaN(t)) seconds = t;
            const d = Number(media.duration);
            if (Number.isFinite(d) && d > 0) duration = d;
        } catch { /* the frame can go away mid-read */ }
    };

    const save = (force) => {
        // Read the LIVE position first. `seconds` is only refreshed on
        // timeupdate, so a pause/ended/teardown save that trusted the cache
        // would write a position up to a second stale — and for `ended`, one
        // nowhere near the end.
        readNow();
        if (!onSave || typeof seconds !== "number" || seconds <= 0) return;
        const now = Date.now();
        if (!force && now - lastSaveAt < SAVE_EVERY_MS) return;
        lastSaveAt = now;
        try { onSave(seconds, duration); } catch { /* best effort */ }
    };

    const wantedStart = () => {
        const v = typeof startAt === "function" ? startAt() : startAt;
        return Number(v) || 0;
    };

    /*
     * Seek to the saved position, as soon as the player will accept it.
     *
     * Retried on every event rather than done once, for two reasons: a DRM
     * player has no usable duration until the licence is in, and the saved
     * positions arrive from their own request which can finish after the player
     * is already running. Trying once and giving up would mean "resume" quietly
     * became "start over" for whichever of those lost the race.
     *
     * It stops as soon as playback is past the opening seconds — by then either
     * we seeked or the member did, and a late resume that yanks someone
     * backwards mid-watch is worse than not resuming at all.
     */
    const resume = () => {
        if (resumed || destroyed || !media) return;
        let at;
        try { at = media.currentTime; } catch { return; }
        if (typeof at === "number" && at > RESUME_WINDOW_S) { resumed = true; return; }
        const target = wantedStart();
        if (!(target > 0)) return; // nothing to resume to yet — look again next event
        const dur = Number(media.duration);
        if (Number.isFinite(dur) && dur > 0 && target >= dur - RESUME_TAIL_S) { resumed = true; return; }
        try { media.currentTime = target; resumed = true; } catch { /* try again next event */ }
    };

    const bind = (player) => {
        const v = player && player.video;
        if (!v) return markUnknown();
        media = v;
        markReady();

        const onTime = () => {
            readNow();
            resume();
            save(false);
        };
        const onMeta = () => { resume(); };
        const onStop = () => { save(true); };

        v.addEventListener("loadedmetadata", onMeta);
        v.addEventListener("play", onTime);
        v.addEventListener("timeupdate", onTime);
        v.addEventListener("pause", onStop);
        v.addEventListener("ended", onStop);
        // The player may already be past metadata by the time we attach.
        resume();

        cleanup = () => {
            v.removeEventListener("loadedmetadata", onMeta);
            v.removeEventListener("play", onTime);
            v.removeEventListener("timeupdate", onTime);
            v.removeEventListener("pause", onStop);
            v.removeEventListener("ended", onStop);
        };
    };

    loadVdoCipherApi()
        .then((Vdo) => {
            if (destroyed) return;
            if (!Vdo || !Vdo.getInstance) return markUnknown();
            let player;
            // getInstance is synchronous in the v2 build the platform embeds and
            // a Promise in newer ones — accept either.
            try { player = Vdo.getInstance(iframe); } catch { return markUnknown(); }
            if (player && typeof player.then === "function") player.then(bind).catch(markUnknown);
            else bind(player);
        })
        .catch(markUnknown);

    return {
        getSeconds: () => {
            // Live, not cached: a note stamps the moment the member clicked,
            // and timeupdate only fires a few times a second.
            readNow();
            return typeof seconds === "number" ? seconds : null;
        },
        seekTo: (secs) => {
            const t = Math.max(0, Number(secs) || 0);
            // A note's timestamp is an explicit request — it must win over the
            // pending resume, which would otherwise fire afterwards and undo it.
            resumed = true;
            try { if (media) media.currentTime = t; } catch { /* ignore */ }
            try { media?.play?.(); } catch { /* ignore */ }
        },
        destroy: () => {
            // Save BEFORE tearing down: leaving a lesson mid-video is exactly
            // when the position matters, and the last timeupdate can be up to
            // SAVE_EVERY_MS old.
            save(true);
            destroyed = true;
            cleanup();
            media = null;
        },
    };
}

/** True for the URL of a VdoCipher player iframe. */
export const isVdoCipherSrc = (src) => /player\.vdocipher\.com/i.test(String(src || ""));
